import React from "react";
import {
  Checkbox,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { User, Response, LoopFeedbackSummary } from "../../types";
import { Check, Rule } from "@mui/icons-material";
import { UserAvatar } from "./UserAvatar";
import { getUserName } from "../../DataTransform";
import { sortUsersByName } from "../../naturalSort";

export type UserActionsType = ({ user }: { user: User }) => React.ReactNode;

export interface Props {
  responses?: Response[];
  setSelected?: (selected: string[]) => void;
  selected?: string[];
  UserActions?: UserActionsType;
  users: User[];
  leader?: string;
  campaignDueDate?: Date | null;
  loopResponses?: LoopFeedbackSummary[];
}

export const UserList = ({
  responses,
  UserActions,
  selected,
  setSelected,
  users,
  leader,
  campaignDueDate,
  loopResponses = [],
}: Props) => {
  const leaderUser = users.find((user) => user.id == leader);

  const isLate = (response: Response) => {
    if (!campaignDueDate || !response.submittedDate) {
      return false;
    }
    return new Date(response.submittedDate) > campaignDueDate;
  };

  function handleToggle(userId: string) {
    if (!selected) {
      return;
    }
    let newChecked = selected;
    if (selected.indexOf(userId) >= 0) {
      newChecked = selected.filter((target) => target !== userId);
    } else {
      newChecked = [...selected, userId];
    }
    setSelected && setSelected(newChecked);
  }

  function Checkmark({ user }: { user: User }) {
    const response = responses?.find(
      (response) => response.authorId === user.id,
    );

    if (response) {
      if (response.submitted) {
        if (isLate(response)) {
          return (
            <Tooltip title="Submitted late">
              <Check htmlColor="var(--color-red)" sx={{ mr: 1 }} />
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title="Submitted">
              <Check sx={{ mr: 1 }} />
            </Tooltip>
          );
        }
      } else if (response.hasAnswers) {
        return (
          <Tooltip title="Started but not submitted">
            <Rule sx={{ mr: 1 }} />
          </Tooltip>
        );
      }
    }
    return <Check sx={{ mr: 1, visibility: "hidden" }} />;
  }

  function Content({ user }: { user: User }) {
    return (
      <>
        {selected && (
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={selected.indexOf(user.id) !== -1}
              tabIndex={-1}
            />
          </ListItemIcon>
        )}
        <Checkmark user={user} />
        <ListItemText
          primary={getUserName(user)}
          // FL-84: the action cluster (reopen + respond + preview + results)
          // is absolutely positioned via secondaryAction. Reserve room for it
          // and allow long names to wrap rather than collide with the icons.
          sx={{ pr: { xs: 14, sm: 18 } }}
          primaryTypographyProps={{ sx: { wordBreak: "break-word" } }}
        />
      </>
    );
  }

  function Button({
    children,
    user,
  }: {
    user: User;
    children: React.ReactNode;
  }) {
    return (
      <ListItemButton
        role={undefined}
        onClick={() => handleToggle(user.id)}
        dense
      >
        {children}
      </ListItemButton>
    );
  }

  // Render users in natural alphabetical order by name (default for user lists).
  const sortedUsers = sortUsersByName(users);

  return (
    <List dense>
      {sortedUsers.map((user, index) => {
        const feedbackSummary = loopResponses.find(
          (summary: LoopFeedbackSummary) => summary.subjectId === user!.id,
        );
        return (
          <ListItem
            key={index}
            secondaryAction={UserActions && UserActions({ user })}
          >
            <ListItemAvatar>
              <UserAvatar
                showHalo={feedbackSummary?.hasStudentFeedback}
                user={user}
                starred={leaderUser && user === leaderUser}
                // eslint-disable-next-line react/prop-types
                showInstructor={user.isInstructor}
              />
            </ListItemAvatar>
            {selected ? (
              <Button user={user}>
                <Content user={user} />
              </Button>
            ) : (
              <Content user={user} />
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

UserList.propTypes = {
  checked: PropTypes.arrayOf(PropTypes.string),
  setSelected: PropTypes.func,
  selected: PropTypes.arrayOf(PropTypes.string),
  UserActions: PropTypes.func,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      email: PropTypes.string,
      headshot: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      ssoId: PropTypes.string,
      isLearner: PropTypes.bool,
      isInstructor: PropTypes.bool,
    }),
  ).isRequired,
  leader: PropTypes.string,
};
